import { createElement, type ElementType, type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

const textVariants = cva("", {
  variants: {
    variant: {
      display:
        "font-display text-[2rem] font-semibold leading-[1.12] tracking-[-0.03em] text-foreground sm:text-3xl",
      title:
        "font-display text-[1.65rem] font-semibold tracking-[-0.03em] text-foreground leading-[1.18]",
      heading:
        "font-display text-[1.125rem] font-semibold tracking-[-0.02em] text-foreground leading-snug",
      body: "text-base text-foreground leading-relaxed",
      muted: "text-[15px] text-foreground-muted leading-relaxed",
      subtle: "text-sm text-foreground-subtle leading-relaxed",
      label:
        "text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground-subtle",
      caption: "text-xs text-foreground-subtle leading-snug",
    },
  },
  defaultVariants: {
    variant: "body",
  },
});

const defaultElements: Record<
  NonNullable<VariantProps<typeof textVariants>["variant"]>,
  ElementType
> = {
  display: "h1",
  title: "h1",
  heading: "h2",
  body: "p",
  muted: "p",
  subtle: "p",
  label: "p",
  caption: "span",
};

export type TextProps = HTMLAttributes<HTMLElement> &
  VariantProps<typeof textVariants> & {
    as?: ElementType;
  };

export function Text({
  className,
  variant = "body",
  as,
  ...props
}: TextProps) {
  const tag = as ?? defaultElements[variant ?? "body"];
  return createElement(tag, {
    className: cn(textVariants({ variant }), className),
    ...props,
  });
}

export { textVariants };
